import React, { useState, useEffect } from 'react';
import { Search, User, Mail, X } from 'lucide-react';
import axios from 'axios';
import { useTenantProtection } from '../../../hooks/useTenantProtection';

const UserSelector = ({ value, onChange, multiple = false, required = false }) => {
  const { getProtectedTenantCode } = useTenantProtection();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    // Initialize selected user IDs from value
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        setSelectedUserIds(value.map(id => parseInt(id)));
      } else if (typeof value === 'string') {
        // Handle comma-separated string
        const ids = value.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
        setSelectedUserIds(ids);
      } else if (typeof value === 'number') {
        setSelectedUserIds([value]);
      }
    } else {
      setSelectedUserIds([]);
    }
  }, [value]);

  useEffect(() => {
    // Filter users based on search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      const filtered = users.filter(user =>
        user.fullName.toLowerCase().includes(term) ||
        user.Email.toLowerCase().includes(term) ||
        user.UserID.toString().includes(term)
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchTerm, users]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const tenantCode = getProtectedTenantCode();
      
      console.log('[UserSelector] Loading users...');
      let response = await axios.get('/api/users', {
        headers: { 'X-Tenant-Code': tenantCode },
      });
      console.log('[UserSelector] Users response:', response.data);
      
      // Handle redirect response for admin users
      if (response.data.redirect) {
        console.log('[UserSelector] Following redirect to:', response.data.redirect);
        response = await axios.get(response.data.redirect, {
          headers: { 'X-Tenant-Code': tenantCode },
        });
        console.log('[UserSelector] Redirected users response:', response.data);
      }
      
      // Handle both array and object with users property
      const usersArray = Array.isArray(response.data) 
        ? response.data 
        : (response.data.users || []);
      
      console.log('[UserSelector] Users array:', usersArray.length, 'users');
      
      const usersData = usersArray.map(u => ({
        ...u,
        UserID: u.UserID || u.userId,
        Email: u.Email || u.email,
        FirstName: u.FirstName || u.firstName || '',
        LastName: u.LastName || u.lastName || '',
        fullName: `${u.FirstName || u.firstName || ''} ${u.LastName || u.lastName || ''}`.trim() || u.username || 'Unknown'
      }));
      
      console.log('[UserSelector] Parsed users:', usersData.length, 'users');
      setUsers(usersData);
      setFilteredUsers(usersData);
    } catch (error) {
      console.error('[UserSelector] Failed to load users:', error);
      console.error('[UserSelector] Error response:', error.response?.data);
      console.error('[UserSelector] Error status:', error.response?.status);
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = (userId) => {
    let newSelection;
    
    if (multiple) {
      // Multiple selection
      if (selectedUserIds.includes(userId)) {
        newSelection = selectedUserIds.filter(id => id !== userId);
      } else {
        newSelection = [...selectedUserIds, userId];
      }
    } else {
      // Single selection
      newSelection = [userId];
      setShowDropdown(false);
    }
    
    setSelectedUserIds(newSelection);
    if (onChange) {
      // For single selection, return just the ID, for multiple return array
      onChange(multiple ? newSelection : (newSelection[0] || null));
    }
  };

  const handleRemoveUser = (userId, e) => {
    e.stopPropagation();
    const newSelection = selectedUserIds.filter(id => id !== userId);
    setSelectedUserIds(newSelection);
    if (onChange) {
      onChange(multiple ? newSelection : (newSelection[0] || null));
    }
  };

  const getSelectedUserDetails = (userId) => {
    return users.find(u => u.UserID === userId);
  };

  return (
    <div className="space-y-2">
      {/* Selected Users Display */}
      {selectedUserIds.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedUserIds.map(userId => {
            const user = getSelectedUserDetails(userId);
            return (
              <div
                key={userId}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-sm"
              >
                <User className="h-3 w-3" />
                <span className="font-medium">
                  {user ? user.fullName : `User #${userId}`}
                </span>
                {user && (
                  <span className="text-xs opacity-75">
                    {user.Email}
                  </span>
                )}
                <button
                  type="button"
                  onClick={(e) => handleRemoveUser(userId, e)}
                  className="ml-1 hover:text-purple-900 dark:hover:text-purple-100"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Search and Dropdown */}
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setShowDropdown(true)}
            placeholder={`Search users... ${selectedUserIds.length > 0 ? `(${selectedUserIds.length} selected)` : ''}`}
            className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:border-purple-500 focus:ring-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 sm:text-sm"
          />
        </div>

        {/* Dropdown */}
        {showDropdown && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-10"
              onClick={() => setShowDropdown(false)}
            />

            {/* Dropdown Menu */}
            <div className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
              {loading ? (
                <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                  Loading users...
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">
                  {searchTerm ? 'No users found matching your search' : 'No users available'}
                </div>
              ) : (
                <div className="py-1">
                  {filteredUsers.map(user => {
                    const isSelected = selectedUserIds.includes(user.UserID);
                    return (
                      <button
                        key={user.UserID}
                        type="button"
                        onClick={() => handleUserSelect(user.UserID)}
                        className={`
                          w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors
                          ${isSelected ? 'bg-purple-50 dark:bg-purple-900/20' : ''}
                        `}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className={`
                              w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                              ${isSelected 
                                ? 'bg-purple-500 text-white' 
                                : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                              }
                            `}>
                              {user.FirstName.charAt(0)}{user.LastName.charAt(0)}
                            </div>
                            <div>
                              <div className={`text-sm font-medium ${isSelected ? 'text-purple-700 dark:text-purple-300' : 'text-gray-900 dark:text-gray-100'}`}>
                                {user.fullName}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center space-x-2">
                                <span className="flex items-center">
                                  <Mail className="h-3 w-3 mr-1" />
                                  {user.Email}
                                </span>
                                <span className="text-gray-400 dark:text-gray-500">•</span>
                                <span>ID: {user.UserID}</span>
                              </div>
                            </div>
                          </div>
                          {isSelected && (
                            <div className="text-purple-500 dark:text-purple-400">✓</div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Helper Text */}
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
        {multiple 
          ? 'Click to select multiple users. User IDs will be used.'
          : 'Click to select a user. User ID will be used.'
        }
        {required && <span className="text-red-500 ml-1">*</span>}
      </p>
    </div>
  );
};

export default UserSelector;
